import React from "react"
import { observer } from "mobx-react-lite"
import {
  View,
  ViewStyle,
  TextStyle,
  Dimensions,
  Linking,
  ListRenderItem,
  TouchableOpacity,
  FlatList
} from "react-native"
import { StackScreenProps } from "@react-navigation/stack"
import { Button, Screen, Text, Svg } from "@app/components"
import { TextProps } from "@app/components/text/text.props"
import { color, spacing } from "@app/theme"
import { Footer } from "@app/components/footer/footer"
import { Feather } from "@expo/vector-icons"
import { textPresets } from "@app/components/button/button.presets"
import { RootNavigatorParamList } from "@app/navigators/navigator-params"

type ListItem = {
  text: any
  preset: TextProps["preset"]
}

const SUPPORT_EMAIL = "kundtjanst@veckanse.com"

type PolicyScreenProps = StackScreenProps<RootNavigatorParamList, "Policy">
export const PolicyScreen = observer((props: PolicyScreenProps) => {
  const goHome = () =>
    props.navigation.navigate("Tabs", { screen: "Home", params: { screen: "Offers" } })

  const renderItem: ListRenderItem<ListItem> = ({ item }) => {
    switch (item.preset) {
      case "bold":
        return (
          <Text preset={item.preset} style={LIST_ITEM_BOLD}>
            {item.text}
          </Text>
        )
      default:
        return (
          <Text preset={item.preset} style={LIST_ITEM_DEFAULT}>
            {item.text}
          </Text>
        )
    }
  }

  const mailLink = (
    <TouchableOpacity onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}>
      <Text style={LINK} text={SUPPORT_EMAIL} />
    </TouchableOpacity>
  )

  return (
    <Screen style={ROOT} preset="scroll">
      <View style={PAGE_WRAPPER}>
        <View style={BACKGROUND_IMAGE}>
          <Svg preset="PinkBlob" />
        </View>
        <Button preset="back" onPress={goHome}>
          <Feather style={textPresets.back} name="chevron-left" />
          <Text style={textPresets.back} text="Tillbaka" />
        </Button>
        <Text style={HEADER_LARGE} preset="headerLarge" text="Integritetspolicy" />
        <Text
          preset="default"
          text="Hur VeckansE behandlar dina personuppgifter"
        />
        <Text
          preset="default"
          text="Cartero Digital Marketing AB | Org.nr 556857–4262 | Box 5077, 102 42 Stockholm"
        />
        <Text preset="default" text="Gäller från: 2026-04-20" />
        <FlatList
          data={[
            { preset: "bold", text: "1. Personuppgiftsansvarig" },
            {
              preset: "default",
              text: "Cartero Digital Marketing AB, org.nr 556857–4262, Box 5077, 102 42 Stockholm (nedan 'VeckansE', 'vi' eller 'oss') är personuppgiftsansvarig för behandling av dina personuppgifter enligt dataskyddsförordningen (GDPR)."
            },
            {
              preset: "default",
              text: (
                <>
                  Kontakt i integritetsfrågor: {mailLink}
                </>
              )
            },
            { preset: "bold", text: "2. Vilka personuppgifter vi samlar in" },
            { preset: "default", text: "Vi samlar in följande kategorier av personuppgifter:" },
            {
              preset: "default",
              text: "• Kontaktuppgifter: e-postadress (obligatorisk), namn (om du uppger det), telefonnummer (om du uppger det)"
            },
            {
              preset: "default",
              text: "• Profiluppgifter: postnummer, ort, preferenser kring erbjudanden och kategorier"
            },
            {
              preset: "default",
              text: "• Ålder: födelseår (om du uppger det) — används för åldersanpassade erbjudanden"
            },
            {
              preset: "default",
              text: "• Användardata: hur du interagerar med våra utskick (öppningar, klick, avregistreringar)"
            },
            {
              preset: "default",
              text: "• Tekniska data: IP-adress, enhetsinformation, cookies och liknande tekniker — se cookiepolicyn"
            },
            {
              preset: "default",
              text: "Vi samlar inte in känsliga personuppgifter (t.ex. hälsoinformation, politiska åsikter eller etniskt ursprung)."
            },
            { preset: "bold", text: "3. Ändamål och rättslig grund" },
            { preset: "default", text: "Vi behandlar dina personuppgifter för följande ändamål:" },
            { preset: "bold", text: "3.1 Leverans av nyhetsbrev och erbjudanden" },
            {
              preset: "default",
              text: "Vi skickar veckans erbjudanden och deals till din e-postadress."
            },
            {
              preset: "default",
              text: "• Rättslig grund: Samtycke (art. 6.1(a) GDPR) — du har aktivt anmält dig till VeckansEs nyhetsbrev."
            },
            {
              preset: "default",
              text: "• Lagringstid: Så länge du är aktiv prenumerant. Vid avregistrering raderas uppgifterna inom 30 dagar, utom de vi är skyldiga att behålla enligt lag."
            },
            { preset: "bold", text: "3.2 Personalisering och segmentering" },
            {
              preset: "default",
              text: "Vi anpassar innehållet i nyhetsbrevet baserat på ditt beteende och dina preferenser."
            },
            {
              preset: "default",
              text: "• Rättslig grund: Samtycke (art. 6.1(a)) och berättigat intresse (art. 6.1(f)) — vi har ett berättigat intresse av att skicka relevant innehåll."
            },
            {
              preset: "default",
              text: "• Profilering: Vi använder öppnings- och klickhistorik, geografisk data och preferenser för att segmentera utskick. Du har rätt att invända mot profilering (se punkt 9)."
            },
            { preset: "bold", text: "3.3 Drift, säkerhet och systemutveckling" },
            {
              preset: "default",
              text: "Vi behandlar tekniska uppgifter för att säkerställa att tjänsten fungerar korrekt och säkert."
            },
            { preset: "default", text: "• Rättslig grund: Berättigat intresse (art. 6.1(f))." },
            { preset: "bold", text: "3.4 Rättsliga skyldigheter" },
            {
              preset: "default",
              text: "I vissa fall är vi skyldiga att behandla uppgifter enligt lag, t.ex. bokföringslagen."
            },
            { preset: "default", text: "• Rättslig grund: Rättslig förpliktelse (art. 6.1(c))." },
            { preset: "bold", text: "4. Cookies" },
            {
              preset: "default",
              text: "Vi använder cookies och liknande tekniker på veckanse.se. Fullständig information finns i vår cookiepolicy. För icke-nödvändiga cookies inhämtas ditt samtycke via vår cookie-banner."
            },
            { preset: "bold", text: "5. Delning av personuppgifter" },
            { preset: "bold", text: "5.1 Samarbetspartner — Smart Senior AB" },
            {
              preset: "default",
              text: "VeckansE samarbetar med Smart Senior AB (org.nr 556801–6900) inom ramen för ett avtal om medlemsöverföring daterat 2025-11-19."
            },
            {
              preset: "default",
              text: "För prenumeranter som är 55 år eller äldre: dina uppgifter (e-postadress, namn och postnummer) kan komma att delas med Smart Senior AB i syfte att du ska kunna ta del av Smart Seniors erbjudanden och tjänster för seniorer."
            },
            {
              preset: "default",
              text: "För prenumeranter med okänd ålder: VeckansE kan skicka information om Smart Seniors erbjudanden till dig för att du ska kunna välja att registrera dig direkt hos Smart Senior."
            },
            {
              preset: "default",
              text: "• Data som delas: e-postadress, namn, postnummer och ålder (där tillgänglig)"
            },
            {
              preset: "default",
              text: "• Ändamål: Smart Senior ska kunna erbjuda dig relevanta tjänster och erbjudanden för seniorer"
            },
            { preset: "default", text: "• Rättslig grund: Berättigat intresse (art. 6.1(f))" },
            {
              preset: "default",
              text: "   – Detta intresse är baserat på den kundrelationella naturen av den tjänst som VeckansE erbjuder och det värde som skapas för Dig genom detta samarbete. Intresset grundar sig således på att kunna leverera en relevant och välanpassad tjänst för VeckansE:s prenumeranter."
            },
            {
              preset: "default",
              text: "• Smart Senior är en separat personuppgiftsansvarig och behandlar dina uppgifter enligt sina egna villkor och integritetspolicy på smartsenior.se"
            },
            {
              preset: "default",
              text: (
                <>
                  • Opt-out: Du kan när som helst invända mot att dina uppgifter delas med Smart
                  Senior AB genom att kontakta oss på {mailLink}
                </>
              )
            },
            { preset: "bold", text: "5.2 Tekniska leverantörer och personuppgiftsbiträden" },
            {
              preset: "default",
              text: "Vi anlitar externa leverantörer som behandlar personuppgifter på våra instruktioner:"
            },
            {
              preset: "default",
              text: "• Apsis: vår e-postplattform (ESP) för hantering av prenumerantdatabasen och utskick. Avtal med adekvata skyddsåtgärder finns."
            },
            {
              preset: "default",
              text: "• Analysverktyg: för att mäta trafik och beteende på veckanse.se. Se cookiepolicyn."
            },
            {
              preset: "default",
              text: "Alla biträden är avtalsbundna att behandla personuppgifter enbart enligt våra instruktioner och GDPR."
            },
            { preset: "bold", text: "5.3 Myndigheter" },
            {
              preset: "default",
              text: "Vi kan lämna ut uppgifter till myndigheter om vi är skyldiga enligt lag."
            },
            { preset: "bold", text: "6. Överföring till tredje land" },
            {
              preset: "default",
              text: "Personuppgifter behandlas i huvudsak inom EU/EES. Om uppgifter överförs till land utanför EU/EES sker det med stöd av lämpliga skyddsåtgärder enligt art. 46 GDPR, t.ex. EU-kommissionens standardavtalsklausuler (SCC)."
            },
            { preset: "bold", text: "7. Lagringstid" },
            {
              preset: "default",
              text: "Vi sparar dina uppgifter så länge som nödvändigt för respektive ändamål:"
            },
            {
              preset: "default",
              text: "• Aktiv prenumeration: uppgifterna sparas så länge du prenumererar"
            },
            {
              preset: "default",
              text: "• Efter avregistrering: uppgifterna raderas inom 30 dagar, undantaget uppgifter vi måste behålla enligt lag (t.ex. bokföringslagen — 7 år)"
            },
            {
              preset: "default",
              text: "• Inaktiva prenumeranter: vi ser regelbundet över och rensar inaktiva prenumeranter"
            },
            { preset: "bold", text: "8. Dina rättigheter" },
            {
              preset: "default",
              text: (
                <>
                  Du har följande rättigheter enligt GDPR. Kontakta oss på {mailLink} för att utöva
                  dem — vi svarar inom 30 dagar.
                </>
              )
            },
            {
              preset: "default",
              text: "• Rätt till tillgång (art. 15): du kan begära en kopia av de uppgifter vi har om dig"
            },
            {
              preset: "default",
              text: "• Rätt till rättelse (art. 16): du kan begära att felaktiga uppgifter korrigeras"
            },
            {
              preset: "default",
              text: "• Rätt till radering (art. 17): du kan begära att dina uppgifter raderas — 'rätten att bli bortglömd'"
            },
            {
              preset: "default",
              text: "• Rätt till begränsning (art. 18): du kan begära att behandlingen begränsas i vissa fall"
            },
            {
              preset: "default",
              text: "• Rätt till dataportabilitet (art. 20): du kan begära att få dina uppgifter i ett maskinläsbart format"
            },
            {
              preset: "default",
              text: "• Rätt att invända (art. 21): du kan invända mot behandling baserad på berättigat intresse, inklusive profilering och delning med Smart Senior AB"
            },
            {
              preset: "default",
              text: "• Rätt att återkalla samtycke: du kan när som helst återkalla ditt samtycke till nyhetsbrevet utan att det påverkar behandlingens laglighet dessförinnan"
            },
            {
              preset: "default",
              text: "Du har rätt att lämna klagomål till Integritetsskyddsmyndigheten (IMY), imy.se, om du anser att vi behandlar dina uppgifter felaktigt."
            },
            { preset: "bold", text: "9. Profilering" },
            {
              preset: "default",
              text: "VeckansE använder profilering för att anpassa och personalisera nyhetsbrevets innehåll till dig. Profileringen baseras på din interaktion med våra utskick (öppningar och klick), geografisk data och eventuella preferenser du angett."
            },
            {
              preset: "default",
              text: "Detta innebär att innehållet är individuellt anpassat baserat på de historiska preferenser som visat sig genom exempelvis öppningar och klick och de angivna preferenserna. Detta medför att innehållet i nyhetsbrevet inte nödvändigtvis är att betrakta som generiskt, utan i stället som individuellt utformat för respektive enskild prenumerant."
            },
            {
              preset: "default",
              text: "Logiken bakom detta är att enhetens användning av nyhetsbrevet spåras, vilket medför att VeckansE får ökad kännedom om vanor och därmed även användarens intressen och behov. Detta är ett led i VeckansE:s strävan att förmedla relevant och uppskattat innehåll till varje enskild prenumerant."
            },
            {
              preset: "default",
              text: (
                <>
                  Du har rätt att invända mot profilering genom att kontakta oss på {mailLink}. Du
                  kan också avregistrera dig från nyhetsbrevet via avregistreringslänken i varje
                  utskick.
                </>
              )
            },
            { preset: "bold", text: "10. Säkerhet" },
            {
              preset: "default",
              text: "Vi vidtar lämpliga tekniska och organisatoriska säkerhetsåtgärder för att skydda dina personuppgifter mot obehörig åtkomst, förlust eller förstöring. Vid en personuppgiftsincident som kan innebära risk för dig anmäler vi detta till IMY inom 72 timmar och informerar dig om det är nödvändigt."
            },
            { preset: "bold", text: "11. Ändringar av integritetspolicyn" },
            {
              preset: "default",
              text: "Vi kan uppdatera denna policy. Vid väsentliga ändringar informerar vi dig via e-post eller tydligt meddelande på veckanse.se. Aktuell version finns alltid tillgänglig på veckanse.se/sekretesspolicy. Versionsnummer och datum för ikraftträdande anges alltid."
            },
            { preset: "bold", text: "12. Kontakt" },
            { preset: "default", text: "Cartero Digital Marketing AB" },
            { preset: "default", text: "Box 5077, 102 42 Stockholm" },
            { preset: "default", text: "Org.nr: 556857–4262" },
            {
              preset: "default",
              text: (
                <>
                  E-post: {mailLink}
                </>
              )
            },
            { preset: "default", text: "Webbplats: veckanse.se" },
            {
              preset: "default",
              text: "Denna integritetspolicy gäller fr.o.m. 2026-04-20"
            }
          ]}
          renderItem={renderItem}
        />
        <View></View>
      </View>
      <Footer />
    </Screen>
  )
})

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  minHeight: Dimensions.get("window").height
}

const LIST_ITEM_BOLD: ViewStyle = {
  marginTop: spacing[4],
  marginBottom: spacing[2]
}
const LIST_ITEM_DEFAULT: ViewStyle = {
  marginLeft: spacing[3],
  marginBottom: spacing[2]
}

const PAGE_WRAPPER: ViewStyle = {
  padding: 24,
  paddingBottom: 80,
  maxWidth: 840,
  minHeight: Dimensions.get("window").height,
  alignSelf: "center"
}

const BACKGROUND_IMAGE: ViewStyle = {
  height: 500,
  width: 580,
  position: "absolute",
  top: -100,
  left: -200,
  transform:
    Dimensions.get("window").width > 768
      ? [{ scale: 2 }, { rotate: "45deg" }]
      : [{ scale: 1 }, { rotate: "45deg" }],
  zIndex: -1
}

const HEADER_LARGE: TextStyle = {
  marginTop: spacing[5],
  marginBottom: spacing[5]
}

const LINK: TextStyle = {
  color: color.palette.pink3
}
